import { ReactNode } from 'react';

import { HandIcon, PhoneIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusIcon } from '@/modules/tryIt/components/StatusIcon/StatusIcon';
import { SynthesisResponseCode } from '@/shared/types/tryIt';

import { DetailRows, PlainRow } from './DetailsRow';
import { GuidelinesLLMDetails } from './GuidelinesLLMDetails';
import { MCPDetails } from './MCPDetails';
import { NoDetailsPanel } from './NoDetailsPanel';
import { TurnHeader } from './TurnHeader';
import { TurnSummary } from './TurnSummary';
import { WorkflowDetails } from './WorkflowDetails';
import {
  selectIsLoadingReplay,
  selectIsReplay,
  selectSelectedTurn,
} from '../../../redux/selectors';
import { ConversationTurnCategoryKind } from '../../../types/conversation';

const STATUS_ROWS: Partial<
  Record<SynthesisResponseCode, { key: string; icon: ReactNode; textKey: string }>
> = {
  [SynthesisResponseCode.INTERRUPTED]: {
    key: 'interrupted',
    icon: (
      <StatusIcon status="attention">
        <HandIcon className="h-4 w-4" />
      </StatusIcon>
    ),
    textKey: 'try_it.interrupted_response',
  },
  [SynthesisResponseCode.VOICEMAIL_DETECTED]: {
    key: 'voicemail',
    icon: <PhoneIcon className="h-4 w-4" />,
    textKey: 'try_it.voicemail_detected_response',
  },
};

const NO_INFO_KINDS = new Set<ConversationTurnCategoryKind>([
  'broadcast',
  'campaign',
  'voice_campaign',
]);

const STEP_KINDS = new Set<ConversationTurnCategoryKind>([
  'workflow',
  'knowledge_base',
  'guidelines_llm',
  'mcp',
  'unknown',
]);

/** Right-column Details panel — routes to a variant based on the selected turn's category. */
export const DetailsPanel = () => {
  const { t } = useTranslation();
  const turn = useSelector(selectSelectedTurn);
  const isLoadingReplay = useSelector(selectIsLoadingReplay);
  const isReplay = useSelector(selectIsReplay);

  // No turn is selected until the replay commits its timeline, so without this
  // the empty state flashes on every open.
  if (isLoadingReplay) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!turn) {
    return <NoDetailsPanel />;
  }

  if (turn.category.kind === 'agent') {
    return <NoDetailsPanel reason="human_agent" />;
  }

  if (NO_INFO_KINDS.has(turn.category.kind) || (isReplay && turn.category.kind === 'no_details')) {
    return <NoDetailsPanel />;
  }

  const hasToolCalls = (turn.metadata.toolCalls ?? []).length > 0;
  const statusRow = hasToolCalls
    ? undefined
    : STATUS_ROWS[turn.metadata.collection?.response_code as SynthesisResponseCode];
  const hasSteps = Boolean(statusRow) || STEP_KINDS.has(turn.category.kind);

  const renderVariant = (grouped = false) => {
    if (statusRow) {
      return (
        <DetailRows rows={[{ ...statusRow, head: <PlainRow>{t(statusRow.textKey)}</PlainRow> }]} />
      );
    }

    switch (turn.category.kind) {
      case 'workflow':
        return <WorkflowDetails turn={turn} grouped={grouped} />;
      case 'knowledge_base':
      case 'guidelines_llm':
        return <GuidelinesLLMDetails turn={turn} grouped={grouped} />;
      case 'mcp':
        return <MCPDetails turn={turn} grouped={grouped} />;
      // An uncategorized brain turn (no node / KB / guideline) still carries a
      // trigger, the considered intents, and a response — the legacy
      // `UnknownDetailsSection` shows these rather than an empty state. Reuse the
      // workflow variant, which renders exactly that (trigger → intent/considered
      // → text).
      case 'unknown':
        return <WorkflowDetails turn={turn} grouped={grouped} />;
      default:
        return <NoDetailsPanel />;
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <TurnHeader turn={turn} />
      <TurnSummary turn={turn} />
      {hasSteps && (
        // A variant can resolve to no rows at all — hide the heading rather than strand it.
        <div className="flex w-full flex-col gap-6 has-[[data-steps-rows]:empty]:hidden">
          <Separator />
          <div className="flex w-full flex-col gap-2">
            <span className="text-sm leading-5 font-semibold text-muted-foreground">
              {t('try_it.steps')}
            </span>
            <div data-steps-rows className="flex w-full flex-col">
              {renderVariant(true)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
